<template>
    <div id="prestataires">
        <div class="app-container mb-5" v-if="!$global.loading">
            <div class="d-flex justify-content-between mt-5">

                <div class="page-title mb-3">
                    <router-link to="/campaigns">
                        <span class="breadcrumb-link">
                            <v-icon class="breadcrumb-link-icon">dashboard</v-icon> Mes campagnes
                        </span>
                    </router-link>
                    <v-icon>keyboard_arrow_right</v-icon>
                    <span class="breadcrumb-item">
                        {{ cadh.name }}
                    </span>
                </div>

                <div class="d-flex gap-10">
                    <v-btn outlined color="primary" class="export-btn" :disabled="loadingExport || !total"
                           @click="prepareExport">
                        <v-icon class="mr-2" v-if="!loadingExport">arrow_downward</v-icon>
                        <v-progress-circular class="progressbar mr-2" indeterminate size="20" v-else color="primary"
                                             width="2">
                        </v-progress-circular>
                        Exporter

                        <div v-if="total">
                            <span>(</span>

                            <span v-if="selectAll || !selectedProviders.length">{{ total }}</span>
                            <span v-else>{{ selectedProviders.length }}</span>

                            <span>)</span>
                        </div>
                    </v-btn>

                    <div class="d-flex">
                        <v-btn v-if="cadh.type === 'OBJ'" outlined color="primary" class="mr-3 text-none"
                               @click="prepareAddProviderModal">
                            <v-icon class="mr-2">add</v-icon>
                            Ajouter un prestataire
                        </v-btn>

                        <v-btn :disabled="cadh.last_batch.state.code !== 'FINISHED'" class="default-button"
                               :to="'/revival/cadh/' + encodeUri(cadh.id)">
                            Relancer
                        </v-btn>
                    </div>
                </div>

            </div>

            <div class="d-flex mt-3 mb-5">
                <div class="mt-auto mb-auto mr-5">
                    Prestataires contactés : <b>{{ cadhProviders }}</b>
                </div>

                <div class="mt-auto mb-auto mr-5">
                    Taux d'ouverture du formulaire : <b>{{ getRate(cadh.opened_form_rate, true) }}</b>
                </div>

                <div class="mt-auto mb-auto mr-5">
                    Taux de réponses : <b>{{ getRate(cadh.answer_rate) }}</b>
                </div>

                <div class="mt-auto mb-auto">
                    Taux d'avancement : <b>{{ getRate(cadh.complete_rate) }}</b>
                </div>
            </div>

            <div class="mt-2 mb-3">
                <v-card>
                    <v-card-title>
                        <filters></filters>

                        <div v-if="selectedProviders.length === options.itemsPerPage"
                             class="text-center w-100 mt-4 presta-count">

                            <div v-if="!selectAll" class="d-flex justify-content-center">
                                <div class="mt-auto mb-auto">
                                    Les <b>{{ selectedProviders.length }}</b> prestataires de cette page sont
                                    sélectionnés.
                                </div>

                                <div>
                                    <v-btn color="primary" text @click="selectAll = true">
                                        Sélectionner les {{ total }} prestataires
                                    </v-btn>
                                </div>
                            </div>

                            <div v-else class="d-flex justify-content-center">
                                <div class="mt-auto mb-auto">
                                    Les <b>{{ total }}</b> prestataires de cette recherche sont sélectionnés.
                                </div>

                                <div>
                                    <v-btn color="primary" text @click="clearSelect">
                                        Annuler la sélection
                                    </v-btn>
                                </div>
                            </div>

                        </div>
                    </v-card-title>

                    <v-data-table :headers="headers" :items="providers" :loading="loading" v-model="selectedProviders"
                                  :items-per-page="options.itemsPerPage" no-data-text="Aucun prestataires"
                                  class="elevation-1"
                                  show-select :server-items-length="total" cadh_answers :sort-by.sync="sortBy"
                                  :sort-desc.sync="sortDesc" :options.sync="options" :footer-props="{
                            itemsPerPageAllText: 'Tous',
                            itemsPerPageText: 'Prestataire par page',
                            itemsPerPageOptions: [10, 20, 50, 1000]
                        }">
                        <template v-slot:item.name="{ item }">
                            <router-link v-if="cadh.type === 'OBJ' && item.name !== null"
                                         :to="'/summary/' + encodeUri(item.id)">
                                {{ item.name }}
                            </router-link>
                            <router-link v-else-if="cadh.type === 'OBJ' && item.name === null"
                                         :to="'/summary/' + encodeUri(item.id)">
                                -
                            </router-link>
                            <span v-else>{{ item.name }}</span>
                        </template>

                        <template v-slot:item.last_web_form.code="{ item }">
                            <div v-if="item.last_web_form.code !== null">
                                <span>{{ item.last_web_form.label }}</span>
                                <span v-if="item.last_web_form.code === 'REFUNDED'">
                                    ({{ formatPrice(item.cadh_answer.refund / 100) }}&nbsp;€)
                                </span>
                                <span v-if="item.cadh_answer !== null">
                                    <v-btn color="primary" icon
                                           :to="'/adhesion/answer/' + encodeUri(item.cadh_answer.id)">
                                        <v-icon>list_alt</v-icon>
                                    </v-btn>
                                </span>
                            </div>
                        </template>

                        <template v-slot:item.cadh_answer.amount="{ item }">
                            <div v-if="item.cadh_answer !== null" class="">
                                {{ formatPrice(item.cadh_answer.amount / 100) }}&nbsp;€
                            </div>
                        </template>

                        <template v-slot:item.cadh_answer.created_at="{ item }">
                            <div v-if="item.cadh_answer !== null">
                                {{ formatDate(item.cadh_answer.created_at, false) }}
                            </div>
                        </template>

                        <template v-slot:item.actions="{ item }">
                            <v-menu bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn icon v-on="on">
                                        <v-icon>mdi-dots-vertical</v-icon>
                                    </v-btn>
                                </template>

                                <v-list>
                                    <v-list-item v-if="item.last_web_form.code === 'SENT' ||
                                    item.last_web_form.code === 'RESENT' || item.last_web_form.code === 'OPENED_FORM'
                                    || item.last_web_form.code === 'NOT_SENT'"
                                                 :href="item.last_web_form.url"
                                                 target="_blank">
                                        <v-list-item-title>
                                            <v-icon color="primary" class="mr-3">remove_red_eye</v-icon>
                                            Voir le
                                            formulaire
                                        </v-list-item-title>
                                    </v-list-item>

                                    <v-list-item v-if="item.last_web_form.code !== 'RESENT' &&
                                    (item.last_web_form.code === 'REFUSED' || item.last_web_form.code === 'SENT' ||
                                    item.last_web_form.code === 'OPENED_FORM'|| item.last_web_form.code === 'NOT_SENT')"
                                                 @click="openResendForm(item)">
                                        <v-list-item-title>
                                            <v-icon color="primary" class="mr-3">send</v-icon>
                                            Renvoyer le
                                            formulaire
                                        </v-list-item-title>
                                    </v-list-item>

                                    <v-list-item
                                      v-if="item.last_web_form.code === 'REFUSED' && item.cadh_answer === null"
                                      @click="updateAnswer(item, 'SENT')">
                                        <v-list-item-title>
                                            <v-icon color="primary" class="mr-3">clear</v-icon>
                                            Annuler le refus
                                        </v-list-item-title>
                                    </v-list-item>

                                    <v-list-item
                                      v-if="item.last_web_form.code === 'REFUSED' && item.cadh_answer !== null"
                                      @click="updateAnswer(item, 'ANSWERED')">
                                        <v-list-item-title>
                                            <v-icon color="primary" class="mr-3">clear</v-icon>
                                            Annuler le refus
                                        </v-list-item-title>
                                    </v-list-item>

                                    <v-list-item v-if="item.cadh_answer !== null" @click="openModifPaiement(item)">

                                        <v-icon color="primary" class="mr-3">edit</v-icon>
                                        Modifier le mode de
                                        paiement
                                    </v-list-item>

                                    <v-list-item v-if="item.cadh_answer !== null && !isInvoiceAvailable"
                                                 @click="openBillNumberModale(item)">
                                        <v-icon color="primary" class="mr-3">edit</v-icon>
                                        Modifier le numéro de
                                        facture
                                    </v-list-item>

                                    <v-list-item v-if="item.cadh_answer !== null" @click="openRefundModale(item)">

                                        <v-icon color="primary" class="mr-3">euro</v-icon>
                                        Modifier le remboursement
                                    </v-list-item>

                                    <v-list-item v-if="item.last_web_form.code === 'SENT' ||
                                    item.last_web_form.code === 'RESENT' ||
                                    item.last_web_form.code === 'ANSWERED' ||
                                    item.last_web_form.code === 'OPENED_FORM' || item.last_web_form.code === 'NOT_SENT'"
                                                 @click="updateAnswer(item, 'REFUSED')">

                                        <v-icon color="primary" class="mr-3">clear</v-icon>
                                        Refuser la demande
                                    </v-list-item>

                                    <v-list-item v-if="item.cadh_answer !== null &&
                                    item.last_web_form.code !== 'REFUSED' &&
                                    item.last_web_form.code !== 'VALIDATED' &&
                                    item.cadh_answer.payment_state.code === 'PAID'"
                                                 @click="updateAnswer(item, 'VALIDATED')">

                                        <v-icon color="primary" class="mr-3">done</v-icon>
                                        Valider la demande
                                    </v-list-item>

                                    <v-list-item v-if="item.cadh_answer !== null && hasStripe &&
                                    item.cadh_answer.transaction_id !== null"
                                                 :href="stripeBaseUrl + item.cadh_answer.transaction_id"
                                                 target="_blank">

                                        <v-icon color="primary" class="mr-3">open_in_new</v-icon>
                                        Voir le paiement
                                        sur
                                        Stripe
                                    </v-list-item>

                                    <v-list-item v-if="item.cadh_answer !== null && item.cadh_answer.paid_at === null"
                                                 @click="updateAnswerPayment(item)">

                                        <v-icon color="primary" class="mr-3">done</v-icon>
                                        Valider le paiement
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                    </v-data-table>
                </v-card>
            </div>


            <v-dialog v-model="showResendModale" width="700px" persistent>
                <v-card v-if="selectedAdhesion !== null && showResendModale">
                    <v-card-title>
                        <h3 class="mb-4">Renvoyer le formulaire ?</h3>
                    </v-card-title>

                    <v-card-text>
                        <div v-if="!selectedAdhesion.cadh_answer">
                            <div class="d-flex pb-3 copy-container gap-10 align-items-center">
                                <v-text-field outlined id="copy-url" type="text" :value="getAdhesionFormLink()">
                                </v-text-field>
                                <v-btn outlined @click="copyUrl" class="copy-btn">
                                    Copier
                                </v-btn>
                            </div>

                            <div id="copied-alert" class="alert alert-success">Lien copié dans le presse-papier</div>
                        </div>

                        <div v-else>
                            <v-alert type="error">
                                Attention, renvoyer le formulaire supprimera la réponse actuelle au formulaire.
                            </v-alert>
                        </div>

                        <div class="d-flex justify-content-end gap-10">
                            <v-btn @click="showResendModale = false">
                                Annuler
                            </v-btn>

                            <v-btn color="primary" @click="resendForm()">
                                Envoyer
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>
            </v-dialog>

            <v-dialog v-model="showModifPaiementModale" width="700px">
                <v-card v-if="selectedAdhesion !== null && showModifPaiementModale">
                    <v-card-title>
                        <h3 class="mb-4">Modifier le mode de paiement</h3>
                    </v-card-title>

                    <v-card-text>
                        <v-select label="Mode de paiement" :items="paymentOptions" outlined return-object
                                  item-text="label" v-model="selectedAdhesion.cadh_answer.payment_option">
                        </v-select>
                    </v-card-text>

                    <v-card-actions class="w-100 d-flex justify-content-between">
                        <v-btn color="error" @click="showModifPaiementModale = false">
                            Annuler
                        </v-btn>

                        <v-btn color="primary" @click="modifyPaymentOption">
                            Enregistrer
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-dialog v-model="showBillNumberModale" width="700px">
                <v-card v-if="selectedAdhesion !== null && showBillNumberModale">
                    <v-card-title>
                        <h3 class="mb-4">Modifier le numéro de facture</h3>
                    </v-card-title>

                    <v-card-text>
                        <v-text-field outlined type="text" label="Numéro de facture"
                                      v-model="selectedAdhesion.cadh_answer.bill_number">
                        </v-text-field>
                    </v-card-text>

                    <v-card-actions class="w-100 d-flex justify-content-between">
                        <v-btn color="error" @click="showBillNumberModale = false">
                            Annuler
                        </v-btn>

                        <v-btn color="primary" @click="modifyBillNumber">
                            Enregistrer
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-dialog v-model="showRefundModale" width="700px">
                <v-card v-if="selectedAdhesion !== null && showRefundModale">
                    <v-card-title>
                        <h3 class="mb-4">Modifier le remboursement</h3>
                    </v-card-title>

                    <v-card-text>
                        <v-text-field outlined type="text" label="Montant (en €)"
                                      v-model="selectedAdhesion.cadh_answer.refund">
                        </v-text-field>
                    </v-card-text>

                    <v-card-actions class="w-100 d-flex justify-content-between">
                        <v-btn color="error" @click="showRefundModale = false">
                            Annuler
                        </v-btn>

                        <v-btn color="primary" @click="modifyRefund">
                            Enregistrer
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-dialog v-model="addProviderModal" width="700px">
                <v-card v-if="addProviderModal">
                    <v-card-title>
                        <h3 class="mb-4">Ajouter un prestataire</h3>
                    </v-card-title>

                    <v-card-text>
                        <v-alert class="mx-auto mt-2" text>
                            Le prestataire recevra automatiquement un e-mail l'invitant à remplir son adhésion.
                        </v-alert>

                        <v-alert class="mx-auto mt-2 bg-error text-white" text v-if="providerToAddEmailInvalid">
                            Ajout impossible, l'email est invalide.
                        </v-alert>

                        <v-combobox label="Prestataire" :items="providersNotInCadh" outlined return-object
                                    item-text="name" v-model="selectedProviderToAdd">
                        </v-combobox>

                        <v-text-field outlined v-if="selectedProviderToAdd !== null" label="E-mail destinataire"
                                      @change="checkNewEmail">
                        </v-text-field>
                    </v-card-text>

                    <v-card-actions class="w-100 d-flex justify-content-between">
                        <v-btn color="error" @click="addProviderModal = false">
                            Annuler
                        </v-btn>

                        <v-btn color="primary" :disabled="selectedProviderToAdd === null ||
                        selectedProviderToAdd.email === undefined ||
                        selectedProviderToAdd.email === null || providerToAddEmailInvalid" @click="addProvider">
                            Ajouter et envoyer
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <input id="input-url" type="text" class="form-control url-input">
            <div id="copied-msg">
                Lien du formulaire copié dans le presse-papier
            </div>

            <confirm ref="confirm"></confirm>
        </div>
    </div>
</template>

<script>
import Filters from "./Filters"
import _ from "lodash"
import { mapMutations, mapState } from "vuex"
import Confirm from "@/components/tools/Confirm"
import * as EmailValidator from "email-validator"

export default {
    name: "ProviderCampaign",
    components: { Confirm, Filters },
    data() {
        return {
            cadhId: null,
            cadh: {},
            providers: [],
            selectAll: false,
            loadingExport: false,
            headers: [
                {
                    text: "Nom",
                    value: "name",
                },
                {
                    text: "Catégorie",
                    value: "category",
                },
                {
                    text: "E-mail",
                    value: "email",
                },
                {
                    text: "Mode de paiement",
                    value: "cadh_answer.payment_option.label",
                },
                {
                    text: "Montant",
                    value: "cadh_answer.amount",
                    width: '90px',
                },
                {
                    text: "Etat du paiement",
                    value: "cadh_answer.payment_state.label",
                },
                {
                    text: "Facture",
                    value: "cadh_answer.bill_number",
                },
                {
                    text: "Formulaire",
                    value: "last_web_form.code",
                },
                {
                    text: "Date de réponse",
                    value: "cadh_answer.created_at",
                },
                {
                    text: "Actions",
                    value: "actions",
                },
            ],
            selectedProviders: [],
            total: null,
            options: { itemsPerPage: 10, page: 1 },
            loading: false,
            selectedContact: null,
            selectedProvider: null,
            selectedProviderToAdd: null,
            addProviderModal: false,
            showResendModale: false,
            showModifPaiementModale: false,
            showBillNumberModale: false,
            showRefundModale: false,
            selectedAdhesion: null,
            providersNotInCadh: [],
            cadhFrontUrl: process.env.VUE_APP_CADH_URL,
            apiUrl: process.env.VUE_APP_API_URL,
            sortBy: "name",
            sortDesc: false,
            cadhProviders: null,
            stripeBaseUrl: "https://dashboard.stripe.com/",
            env: process.env.NODE_ENV,
            hasStripe: null,
            providerToAddEmailInvalid: false,
        }
    },
    methods: {
        ...mapMutations("cadhFilter", ["setCadh"]),
        async getCadh() {
            this.$global.loading = true

            let res = await this.$http.get("auth/campaigns/cadh/" + this.cadhId)

            this.cadh = res.body.data.cadh
            this.cadhProviders = res.body.data.cadh.providers_count
            this.hasStripe = res.body.data.hasStripe

            this.setCadh(this.cadh)

            if (this.cadh.type === "ENT") {
                this.headers = this.headers.filter((item) => {
                    return item.value !== "category"
                })
            }

            this.$global.loading = false
        },
        getProviders: _.debounce(function() {
            if (this.cadh.type === "OBJ") {
                this.getObjects()
            } else {
                this.getEntities()
            }
        }, 500),
        getObjects() {
            let sortBy = Array.isArray(this.sortBy) ? this.sortBy[0] : this.sortBy
            let sortDesc = Array.isArray(this.sortDesc) ? this.sortDesc[0] : this.sortDesc

            let payload = {
                perPage: this.options.itemsPerPage,
                currentPage: this.options.page,
                search: this.search,
                emailSearch: this.emailSearch,
                categories: this.selectedCategories,
                communes: this.selectedCommunes,
                tags: this.selectedTags,
                states: this.selectedWebFormStates,
                paymentOptions: this.selectedPaymentOptions,
                paymentStates: this.selectedPaymentStates,
                optionTypes: this.selectedOptionTypes,
                cadhId: this.cadhId,
                sortBy: sortBy,
                sortDesc: sortDesc,
            }

            this.loading = true

            this.$http.post("auth/providers", payload).then(res => {
                this.providers = Object.values(res.body.data.providers.data)
                this.currentPage = res.body.data.providers.from
                this.total = res.body.data.providers.total

                this.selectedProviders = []

                this.loading = false
            })
        },
        getEntities() {
            let sortBy = Array.isArray(this.sortBy) ? this.sortBy[0] : this.sortBy
            let sortDesc = Array.isArray(this.sortDesc) ? this.sortDesc[0] : this.sortDesc

            let payload = {
                perPage: this.options.itemsPerPage,
                currentPage: this.options.page,
                search: this.search,
                emailSearch: this.emailSearch,
                tags: this.selectedTags,
                states: this.selectedWebFormStates,
                paymentOptions: this.selectedPaymentOptions,
                paymentStates: this.selectedPaymentStates,
                optionTypes: this.selectedOptionTypes,
                cadhId: this.cadhId,
                sortBy: sortBy,
                sortDesc: sortDesc,
            }

            this.loading = true

            this.$http.post("auth/entities", payload).then(res => {
                this.providers = Object.values(res.body.data.entities.data)
                this.currentPage = res.body.data.entities.from
                this.total = res.body.data.entities.total

                this.selectedProviders = []

                this.loading = false
            })
        },
        slugify(str) {
            str = str.replace(/^\s+|\s+$/g, "")
            str = str.toLowerCase()

            let from = "àáäâèéëêìíïîòóöôùúüûñç·/_,:;"
            let to = "aaaaeeeeiiiioooouuuunc------"

            for (let i = 0, l = from.length; i < l; i++) {
                str = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i))
            }

            return str.replace(/[^a-z0-9 -]/g, "")
              .replace(/\s+/g, "-")
              .replace(/-+/g, "-")
        },
        encodeUri(id) {
            return btoa(id)
        },
        openResendForm(provider) {
            this.selectedAdhesion = provider
            this.showResendModale = true
            // if (provider.cadh_answer === null) {
            //     this.selectedAdhesion = provider
            //     this.showResendModale = true
            // } else {
            //     this.$refs.confirm.open("Attention",
            //       "Renvoyer le formulaire supprimera la réponse actuelle au formulaire, " +
            //       "êtes-vous sûre de vouloir continuer ?",
            //       { color: "red" }).then((confirm) => {
            //         if (confirm) {
            //             this.selectedAdhesion = provider
            //             this.showResendModale = true
            //         }
            //     })
            // }
        },
        openBillNumberModale(provider) {
            this.selectedAdhesion = JSON.parse(JSON.stringify(provider))
            this.showBillNumberModale = true
        },
        openRefundModale(provider) {
            this.selectedAdhesion = JSON.parse(JSON.stringify(provider))

            if (this.selectedAdhesion.cadh_answer.refund !== null) {
                this.selectedAdhesion.cadh_answer.refund /= 100
            }

            this.showRefundModale = true
        },
        getAdhesionFormLink() {
            return this.selectedAdhesion.last_web_form.url
        },
        copyUrl() {
            let url = document.getElementById("copy-url")

            url.select()
            document.execCommand("copy")

            let alert = document.getElementById("copied-alert")

            if (alert !== null) {
                alert.classList.add("show")

                setTimeout(() => {
                    alert.classList.remove("show")
                }, 5000)
            }
        },
        resendForm() {
            let payload = {
                cadhFormId: this.selectedAdhesion.last_web_form.id,
            }

            this.$global.loading = true

            this.$http.post("auth/campaigns/cadh/resend-form", payload).then(res => {
                this.showResendModale = false
                this.getProviders()
                this.$global.loading = false
            })
        },
        openModifPaiement(provider) {
            this.selectedAdhesion = JSON.parse(JSON.stringify(provider))
            this.showModifPaiementModale = true
        },
        getPaymentOptions() {
            let hash = this.$route.params.hash
            let cadhId = parseInt(atob(hash))

            this.$http.get("auth/campaigns/cadh/payment-options/" + cadhId).then(res => {
                this.paymentOptions = res.body.data.payment_options
            })
        },
        modifyPaymentOption() {
            let payload = {
                answerId: this.selectedAdhesion.cadh_answer.id,
                paymentOptionId: this.selectedAdhesion.cadh_answer.payment_option.id,
            }

            this.$global.loading = true

            this.$http.post("auth/campaigns/cadh/payment-option", payload).then(res => {
                this.$global.loading = false
                this.showModifPaiementModale = false

                this.getProviders()
            })
        },
        modifyBillNumber() {
            let payload = {
                answerId: this.selectedAdhesion.cadh_answer.id,
                billNumber: this.selectedAdhesion.cadh_answer.bill_number,
            }

            this.$global.loading = true

            this.$http.post('auth/campaigns/cadh/bill-number', payload).then(res => {
                this.$global.loading = false
                this.showBillNumberModale = false

                this.getProviders()
            })
        },
        modifyRefund() {
            let payload = {
                answerId: this.selectedAdhesion.cadh_answer.id,
                refund: this.selectedAdhesion.cadh_answer.refund,
            }

            this.$global.loading = true

            this.$http.post("auth/campaigns/cadh/refund", payload).then(res => {
                this.$global.loading = false
                this.showRefundModale = false

                this.getProviders()
            })
        },
        updateAnswer(provider, state) {
            let word = state === "VALIDATED" ? "valider" : "refuser"

            if (state === "SENT") {
                word = "réouvrir"
            }

            this.$refs.confirm.open(word.charAt(0).toUpperCase() + word.slice(1) + " la demande",
              "Êtes-vous sûr de vouloir " + word + " la demande ?",
              { color: "red", backgroundTitle: "#4285F4" }).then((confirm) => {
                if (confirm) {
                    this.$global.loading = true

                    let payload = {
                        cadhFormId: provider.last_web_form.id,
                        state: state,
                        origin: 'OT',
                    }

                    this.$http.post("auth/campaigns/cadh/answer/update-state", payload).then(res => {
                        this.$global.loading = false

                            this.getCadh()
                            this.getProviders()
                        })
                    }
                })
        },
        updateAnswerPayment(provider) {
            this.$refs.confirm.open("Valider le paiement",
              "Êtes-vous sûr de vouloir valider le paiement ?",
              { color: "red", backgroundTitle: "#4285F4" }).then((confirm) => {
                if (confirm) {
                    this.$global.loading = true

                    let payload = {
                        answerId: provider.cadh_answer.id,
                    }

                    this.$http.post("auth/campaigns/cadh/answer/validate-paiement", payload).then(res => {
                        this.$global.loading = false

                        this.getProviders()
                    })
                }
            })
        },
        openForm() {
            let hash = btoa(this.selectedAdhesion.cadh_answer.id)
            let url = this.cadhFrontUrl + "/adhesion/update/" + hash

            window.open(url)
        },
        prepareAddProviderModal() {
            this.selectedProviderToAdd = null

            this.getProvidersNotInCadh()
        },
        getProvidersNotInCadh() {
            this.$http.get("auth/campaigns/cadh/providers/not-in-cadh/" + this.cadh.last_batch.id).then(res => {
                this.providersNotInCadh = res.body.data.providers

                this.addProviderModal = true
            })
        },
        addProvider() {
            this.$global.loading = true

            setTimeout(() => {
                let email = this.selectedProviderToAdd.email.email !== undefined ?
                  this.selectedProviderToAdd.email.email :
                  this.selectedProviderToAdd.email

                let payload = {
                    providerId: this.selectedProviderToAdd.id,
                    email: email,
                    batchId: this.cadh.last_batch.id,
                }

                this.$http.post("auth/campaigns/cadh/providers/add", payload).then(res => {
                    this.$global.loading = false

                    this.getProviders()

                    this.addProviderModal = false
                })

            }, 100)
        },
        getRate(number, openRate = false) {
            if (openRate && new Date(this.cadh.created_at).getTime() < new Date("2023-09-26").getTime()) {
                return "Non disponible"
            }

            if (number === null) {
                return "0%"
            }
            return Math.round(number * 100) + "%"
        },
        clearSelect() {
            this.selectedProviders = []
            this.selectAll = false
        },
        async getProviderIds(nothingMeanAll = false) {
            let providerIds = []

            if (!this.selectAll && (!nothingMeanAll || this.selectedProviders.length)) {
                providerIds = this.selectedProviders.map(provider => provider.id)
            } else {
                let payload = {
                    perPage: this.options.itemsPerPage,
                    currentPage: this.options.page,
                    search: this.search,
                    emailSearch: this.emailSearch,
                    categories: this.selectedCategories,
                    communes: this.selectedCommunes,
                    tags: this.selectedTags,
                    states: this.selectedWebFormStates,
                    onlyIds: true,
                    cadhId: this.cadhId,
                    paymentOptions: this.selectedPaymentOptions,
                    paymentStates: this.selectedPaymentStates,
                    optionTypes: this.selectedOptionTypes,
                }

                let res = await this.$http.post("auth/providers", payload)

                providerIds = res.body.data.ids

                this.$global.loading = false
            }

            return providerIds
        },
        async getEntityIds(nothingMeanAll = false) {
            let entityIds = []

            if (!this.selectAll && (!nothingMeanAll || this.selectedProviders.length)) {
                entityIds = this.selectedProviders.map(provider => provider.id)
            } else {
                let payload = {
                    perPage: this.options.itemsPerPage,
                    currentPage: this.options.page,
                    search: this.search,
                    emailSearch: this.emailSearch,
                    states: this.selectedWebFormStates,
                    onlyIds: true,
                    cadhId: this.cadhId,
                    paymentOptions: this.selectedPaymentOptions,
                    paymentStates: this.selectedPaymentStates,
                    optionTypes: this.selectedOptionTypes,
                }

                let res = await this.$http.post("auth/entities", payload)

                entityIds = res.body.data.ids

                this.$global.loading = false
            }

            return entityIds
        },
        async prepareExport() {
            this.loadingExport = true

            let providerIds = []
            let entityIds = []

            if (this.cadh.type === "OBJ") {
                providerIds = await this.getProviderIds(true)
            } else {
                entityIds = await this.getEntityIds(true)
            }

            let payload = {
                providerIds: providerIds,
                entityIds: entityIds,
                cadhId: this.cadhId,
                states: this.selectedWebFormStates,
            }

            let res = await this.$http.post("auth/campaigns/cadh/export/generate", payload)
            let filename = res.body.data.filename

            this.loadingExport = false

            let a = document.createElement("a")

            a.href = this.apiUrl + "/export-to-xls/" + filename
            a.click()
        },
        formatPrice(price) {
            return price.toFixed(2).toString().replace('.', ',')
        },
        checkNewEmail(contact) {
            if(this.selectedProviderToAdd) {
                this.selectedProviderToAdd.email = contact
                this.providerToAddEmailInvalid = !EmailValidator.validate(this.selectedProviderToAdd.email)
            } else {
                this.providerToAddEmailInvalid = false
            }
        },
    },
    computed: {
        ...mapState("cadhFilter",
          ["search", "selectedCategories", "selectedWebFormStates", "emailSearch",
              "selectedTags", "selectedCommunes", "selectedPaymentOptions", "selectedPaymentStates",
              "selectedOptionTypes"],
        ),
        ...mapState("account",
          ["user", "account"],
        ),
        isInvoiceAvailable() {
            let available = false
            let contracts = this.account.current_contracts

            if (contracts.length) {

                let module = contracts.find(offer => offer.contract_offer.code === "ADHESION_INVOICE")

                if (module) {
                    available = true
                }

            }
            return available
        },
    },
    watch: {
        options() {
            this.getProviders()
        },
        search() {
            this.options.page = 1
            this.getProviders()
        },
        emailSearch() {
            this.options.page = 1
            this.getProviders()
        },
        selectedCategories() {
            this.options.page = 1
            this.getProviders()
        },
        selectedCommunes() {
            this.options.page = 1
            this.getProviders()
        },
        selectedTags() {
            this.options.page = 1
            this.getProviders()
        },
        selectedWebFormStates() {
            this.options.page = 1
            this.getProviders()
        },
        selectedPaymentOptions() {
            this.options.page = 1
            this.getProviders()
        },
        selectedPaymentStates() {
            this.options.page = 1
            this.getProviders()
        },
        selectedOptionTypes() {
            this.options.page = 1
            this.getProviders()
        },
        selectedProviders() {
            if (this.selectedProviders.length !== this.options.itemsPerPage) {
                this.selectAll = false
            }
        },
        selectedProviderToAdd() {
            if(this.selectedProviderToAdd) {
                this.providerToAddEmailInvalid = !EmailValidator.validate(this.selectedProviderToAdd.email)
            } else {
                this.providerToAddEmailInvalid = false
            }
        },
    },
    filters: {
        date(value) {
            return this.$moment(value).format("DD/MM/YYYY à HH:mm")
        },
    },
    async created() {
        let hash = this.$route.params.hash
        this.cadhId = parseInt(atob(hash))

        await this.getCadh()
        this.getProviders()
        this.getPaymentOptions()

        if (["development", "qualif", "dev"].includes(this.env)) {
            this.stripeBaseUrl += "test/"
        }

        this.stripeBaseUrl += "payments/"

        this.$global.$on("load-providers", () => {
            this.$store.dispatch("account/fetchTags")

            this.getProviders()
        })
    },
    beforeDestroy() {
        this.$global.$off("close-update-modal")
        this.$global.$off("load-providers")
    },
}
</script>